import { useEffect, useState } from "react";
import api from "@/lib/api";
import safeStorage from "@/lib/safeStorage";
import StaffLayout from "@/components/StaffLayout";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Check, Building2, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminRestaurants() {
  const { t } = useLang();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [active, setActive] = useState(safeStorage.getItem("restaurant_id") || "");

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? This removes its tables, menu, orders and staff.`)) return;
    try {
      await api.delete(`/restaurants/${r.id}`);
      toast.success(`${r.name} deleted`);
      if (r.id === active) {
        safeStorage.removeItem("restaurant_id");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const load = async () => {
    const { data } = await api.get("/restaurants");
    setList(data);
    if (!active && data.length) {
      setActive(data[0].id);
      safeStorage.setItem("restaurant_id", data[0].id);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/restaurants", { name: name.trim() });
      toast.success(`${data.name} created`);
      setName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    }
  };

  const switchTo = (id) => {
    safeStorage.setItem("restaurant_id", id);
    setActive(id);
    toast.success("Switched restaurant");
    setTimeout(() => window.location.reload(), 400);
  };

  return (
    <StaffLayout>
      <div className="overline text-zinc-500">{t("admin")}</div>
      <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("restaurants_title")}</h1>
      <p className="font-plex text-sm text-zinc-400 mt-2 max-w-xl">
        {t("restaurants_desc")}
      </p>

      {isOwner && (
        <form onSubmit={create} className="mt-8 flex gap-3 items-end" data-testid="new-restaurant-form">
          <div className="flex-1 max-w-md">
            <label className="overline text-zinc-500">{t("new_restaurant_name_lbl")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 px-3 py-2.5 w-full font-plex text-sm" data-testid="new-restaurant-name" placeholder="Trattoria del Sole" required />
          </div>
          <button type="submit" data-testid="add-restaurant-btn" className="bg-white text-black px-4 py-2.5 font-plex text-sm hover:bg-[#007aff] hover:text-white transition-colors flex items-center gap-2">
            <Plus size={14} /> {t("create")}
          </button>
        </form>
      )}

      <div className="mt-10 border border-[#262626]">
        {list.map((r) => (
          <div key={r.id} className="px-4 py-4 border-b border-[#262626] last:border-0 flex items-center gap-4" data-testid={`restaurant-row-${r.id}`}>
            <Building2 size={16} className={r.id === active ? "text-[#32d74b]" : "text-zinc-500"} />
            <div className="flex-1">
              <div className="font-plex text-sm text-white">{r.name}</div>
              <div className="overline text-zinc-500">{t("owner_lbl")} · {r.owner_email}</div>
            </div>
            {r.id === active ? (
              <span className="inline-flex items-center gap-1.5 border border-[#32d74b] text-[#32d74b] px-3 py-1.5 text-xs font-plex">
                <Check size={12} /> {t("active")}
              </span>
            ) : (
              <button onClick={() => switchTo(r.id)} data-testid={`switch-restaurant-${r.id}`} className="border border-[#262626] px-3 py-1.5 text-xs font-plex hover:bg-white hover:text-black transition-colors">
                {t("switch_to")}
              </button>
            )}
            {isOwner && (
              <button onClick={() => remove(r)} data-testid={`delete-restaurant-${r.id}`} className="border border-[#262626] px-3 py-1.5 text-xs font-plex hover:bg-[#ff3b30] hover:text-white hover:border-[#ff3b30] transition-colors flex items-center gap-1.5" title={t("remove")}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
    </StaffLayout>
  );
}
